import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { Button } from 'antd';
import axios from 'axios';
import AuthShell from './AuthShell';
import OtpInput from './OtpInput';
import { verifyEmailOtp, requestEmailOtp } from './authApi';
import {
  readReturnPath,
  clearReturnPath,
  sanitizeReturnPath,
  isWorkspaceScoped,
  resolveReturnTarget,
} from './returnPath';

const RESEND_COOLDOWN = 60; // seconds
const MAX_RESENDS = 3;

// The signed-in user's workspaces, or null when they can't be fetched — the
// caller then falls back to what it already knows.
const fetchWorkspaces = () =>
  axios
    .get('/api/workspaces', { withCredentials: true })
    .then((res) =>
      res.data && res.data.success && Array.isArray(res.data.workspaces)
        ? res.data.workspaces
        : null
    )
    .catch(() => null);

// Enjo-style email verification: enter the 6-char code we emailed.
const EmailVerification = (props) => {
  const nav = (props.location && props.location.state) || {};
  const [sessionId, setSessionId] = useState(nav.sessionId);
  const email = nav.email;
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN);
  const [resendsLeft, setResendsLeft] = useState(MAX_RESENDS);

  // Passed in route state by the sign-in/up screens; falls back to the stored
  // path after a reload.
  const returnPath = (props.location.state || {}).returnTo || readReturnPath('');

  // No session in route state (e.g. page refresh) -> back to sign-in.
  useEffect(() => {
    if (!sessionId || !email) props.history.replace('/troopr/access');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const verify = async () => {
    setError('');
    if (otp.length !== 6) return setError('Enter the 6-character code');
    setLoading(true);
    try {
      const res = await verifyEmailOtp({ sessionId, otp });
      if (res.data && res.data.success) {
        // Cookies are set. Head back to the route the user asked for; the
        // workspace chooser is only the fallback.
        //
        // A workspace-scoped path can open as-is. An unscoped one still needs a
        // workspace: the remembered one if it can open the path, else the only
        // one that can. Otherwise the chooser, which replays the path once they
        // pick — so it is kept, not cleared, on that route.
        const safePath = sanitizeReturnPath(returnPath);
        const workspaces =
          safePath && !isWorkspaceScoped(safePath) ? await fetchWorkspaces() : null;
        const target = resolveReturnTarget(safePath, {
          rememberedWorkspaceId: localStorage.getItem('userCurrentWorkspaceId'),
          workspaces,
        });
        if (target) {
          if (target.workspaceId) {
            localStorage.setItem('userCurrentWorkspaceId', target.workspaceId);
          }
          clearReturnPath();
          // replace, so Back does not return to a spent code screen.
          window.location.replace(target.path);
        } else {
          window.location.href = '/user-workspaces';
        }
      } else {
        setLoading(false);
        setError((res.data && res.data.message) || 'Invalid code');
      }
    } catch (e) {
      setLoading(false);
      setError('Something went wrong. Please try again.');
    }
  };

  const resend = async () => {
    if (cooldown > 0 || resendsLeft <= 0) return;
    try {
      const res = await requestEmailOtp(email, { isSignup: !!nav.isSignup });
      if (res.data && res.data.success) {
        setSessionId(res.data.sessionId);
        setOtp('');
        setError('');
        setCooldown(RESEND_COOLDOWN);
        setResendsLeft((n) => n - 1);
      }
    } catch (e) {
      setError('Could not resend code. Try again.');
    }
  };

  return (
    <AuthShell>
      <h1 className="tpr-auth__title">Check your email</h1>
      <p className="tpr-auth__subtitle">
        We sent a 6-character code to <strong className="tpr-auth__email">{email}</strong>
      </p>

      {error && <span className="tpr-auth__error">* {error}</span>}
      <OtpInput value={otp} onChange={setOtp} error={!!error} />

      <div style={{ marginTop: 18 }}>
        <Button
          className="tpr-auth__primary"
          type="primary"
          loading={loading}
          onClick={verify}
        >
          Verify and continue
        </Button>
      </div>

      <div className="tpr-auth__resend">
        {resendsLeft <= 0 ? (
          <span>No resends left. Please start over.</span>
        ) : cooldown > 0 ? (
          <span>Resend code in {cooldown}s</span>
        ) : (
          <span className="tpr-auth__link" onClick={resend}>
            Resend code
          </span>
        )}
      </div>
    </AuthShell>
  );
};

export default withRouter(EmailVerification);
